import React, { ReactElement, useMemo, useState } from 'react';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import useInitialData from '../hooks/global/useInitialData';
import WindowResource from '../services/resources/WindowResource';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import pageDiagnostics from '../services/helpers/PageDiagnostics';
import InitialLoadingManager from '../services/api/InitialLoadingManager';

interface Props {
  children: ReactElement[];
}

export interface CompanySlugContextInterface {
  slug: null | string;
  loading: boolean;
  updateSlug: (value: string) => Promise<boolean>;
}

export const CompanySlugContext = React.createContext<CompanySlugContextInterface>(null);

export default function CompanySlugProvider(props: Props) {
  const { children } = props;

  const { forceRefresh } = useInitialData();
  const { isMobileApp } = useMobileApp();

  const [slug, setSlug] = useState<string>(null);
  const [loading, setLoading] = useState(isMobileApp ? false : true);

  const updateSlug = async (value: string): Promise<boolean> => {
    if (slug === value) {
      return;
    }

    setSlug(value);
    setLoading(true);
    // No slug in the route (the root, a table's link): the saved one is tried, and the franchise's
    // default restaurant if that was renamed or deleted since. A slug from the URL stays a 404.
    const request = await forceRefresh(value, true, !value);
    const { success, status } = request;
    setLoading(false);

    if (!success && status === 404) {
      // A deleted company's slug is a stale link, not a fault: it still goes to /404, unreported.
      if (!InitialLoadingManager.isDeletedCompany(request)) {
        pageDiagnostics.companySlugNotFound(value);
      }

      ReduxActions.dispatch(
        actionCreators.initialData.updateCompany({
          slug: null,
        }),
      );

      !WindowResource.isNotFoundPage() && WindowResource.replace('/404');

      return success;
    }

    return success;
  };

  const context: CompanySlugContextInterface = useMemo(
    () => ({
      slug,
      loading,
      updateSlug,
    }),
    [slug, loading, setLoading],
  );

  return <CompanySlugContext.Provider value={context}>{children}</CompanySlugContext.Provider>;
}
